import { useState } from 'react';
import { Panel, Button, Skeleton, EmptyState } from '@/components/ui';
import { AreaSpark, RateChart } from '@/components/viz';
import { useMarketRates, type MarketRate } from '@/hooks/use-market-rates';

function dirOf(bps: number | null): 1 | -1 | 0 {
  if (bps === null) return 0;
  if (bps > 0) return 1;
  if (bps < 0) return -1;
  return 0;
}

function formatChange(bps: number | null): string {
  if (bps === null) return '—';
  if (bps === 0) return 'flat';
  const sign = bps > 0 ? '+' : '';
  return `${sign}${bps}bp`;
}

function colorFor(dir: 1 | -1 | 0): string {
  if (dir > 0) return 'var(--color-accent)';
  if (dir < 0) return 'var(--color-danger)';
  return 'var(--color-text-dim)';
}

export function MarketRates() {
  const [hoverRate, setHoverRate] = useState(-1);
  const [selectedRate, setSelectedRate] = useState(-1);
  const { data, isLoading, isError, error, refetch } = useMarketRates();

  if (isLoading) {
    return (
      <Panel title="Market Rates" right={<span className="text-xs text-text-dim font-mono">30d trend</span>}>
        <div className="flex flex-col gap-1 p-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-6" />
          ))}
        </div>
      </Panel>
    );
  }

  if (isError) {
    return (
      <Panel title="Market Rates" right={<span className="text-xs text-danger font-mono">unavailable</span>}>
        <EmptyState
          title="Rates unavailable"
          description={error instanceof Error ? error.message : 'Failed to load market rates'}
          action={<Button onClick={() => { refetch(); }}>Retry</Button>}
        />
      </Panel>
    );
  }

  const rates: MarketRate[] = data?.rates ?? [];
  if (rates.length === 0) {
    return (
      <Panel title="Market Rates">
        <EmptyState title="No rates" description="Market rates will appear here once available." />
      </Panel>
    );
  }

  const warnings = data?.warnings ?? [];
  return (
    <Panel
      title="Market Rates"
      right={
        selectedRate >= 0
          ? <span onClick={() => setSelectedRate(-1)} className="text-xs text-accent font-mono cursor-pointer">&larr; All rates</span>
          : <span className="text-xs text-text-dim font-mono">30d trend</span>
      }
    >
      <div className="flex flex-col flex-1 min-h-0 gap-1">
        {warnings.length > 0 && (
          <div className="text-[10px] font-mono text-warn px-1" data-testid="market-rates-warnings">
            ⚠ {warnings.length === 1 ? warnings[0] : `${warnings.length} rates showing cached values`}
          </div>
        )}
        {selectedRate >= 0 && (() => {
          const r = rates[selectedRate];
          const dir = dirOf(r.change_bps);
          const clr = colorFor(dir);
          // RateChart needs { date, value } tuples; spark_data is number[].
          // Synthesize relative-day labels ending at `as_of`.
          const chartData = r.spark_data.map((value, idx, arr) => ({
            date: idx === arr.length - 1 ? r.as_of : `T-${arr.length - 1 - idx}`,
            value,
          }));
          return (
            <div className="flex-1 flex flex-col min-h-0 border-b border-panel-border pb-1">
              <RateChart data={chartData} color={clr} />
            </div>
          );
        })()}
        <div
          className="flex flex-col gap-px"
          style={{ flex: selectedRate >= 0 ? 'none' : 1, justifyContent: 'space-between' }}
        >
          {rates.map((r, i) => {
            const dir = dirOf(r.change_bps);
            const clr = colorFor(dir);
            const hovered = hoverRate === i;
            const selected = selectedRate === i;
            return (
              <div
                key={r.rate_type}
                title={r.source_detail}
                onMouseEnter={() => setHoverRate(i)}
                onMouseLeave={() => setHoverRate(-1)}
                onClick={() => setSelectedRate((prev) => (prev === i ? -1 : i))}
                className="flex items-center rounded-[2px] cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.2,0,0,1)]"
                style={{
                  height: selectedRate >= 0 ? 'auto' : '100%',
                  padding: selectedRate >= 0 ? '2px 4px' : '0 4px',
                  background: selected
                    ? `color-mix(in srgb, ${clr} 7%, transparent)`
                    : hovered
                      ? `color-mix(in srgb, ${clr} 3%, transparent)`
                      : 'transparent',
                  borderLeft: selected ? `2px solid ${clr}` : '2px solid transparent',
                }}
              >
                <div className="w-[110px] shrink-0">
                  <span
                    className="text-[11px] font-body transition-colors duration-300"
                    style={{
                      fontWeight: selected ? 600 : 500,
                      color: selected || hovered ? 'var(--color-text-bright)' : 'var(--color-text)',
                    }}
                  >
                    {r.display_name}
                  </span>
                </div>
                <span
                  className="text-[11px] font-mono w-16 shrink-0 tabular-nums transition-colors duration-300"
                  style={{ color: selected || hovered ? 'var(--color-text-bright)' : 'var(--color-text-muted)' }}
                >
                  {r.value.toFixed(2)}%
                </span>
                <div className="flex-1 flex justify-center">
                  <AreaSpark
                    color={clr}
                    data={r.spark_data}
                    w={110}
                    h={selectedRate >= 0 ? 20 : 30}
                    dir={dir}
                    active={hovered || selected}
                  />
                </div>
                <span
                  className="text-[9px] font-mono w-12 text-right shrink-0 tabular-nums transition-all duration-300"
                  style={{
                    color: (hovered || selected) ? clr : (dir !== 0 ? clr : 'var(--color-text-dim)'),
                    fontWeight: (hovered || selected) ? 700 : (dir !== 0 ? 600 : 400),
                    textShadow: (hovered || selected) && dir !== 0 ? `0 0 6px color-mix(in srgb, ${clr} 20%, transparent)` : 'none',
                  }}
                >
                  {formatChange(r.change_bps)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}
